/**
 * UI plumbing (spec §16): the toast timer with hover-hold, and the single prompt/confirm dialog
 * store. `dialog` is exported as a singleton next to `store`.
 */
import type { I18nKey } from '$lib/i18n';
import type { MailState } from './state.svelte';

export const TOAST_MS = 3500;
export const TOAST_UNDO_MS = 10_000;
export const TOAST_MIN_LEFT_MS = 600;

const nowMs = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/** Toast behaviour over the root state (`toast`, `toastLeft`, `toastAt`, `undoToken`). */
export class ToastModule {
  private timer: ReturnType<typeof setTimeout> | undefined;
  constructor(private readonly s: MailState) {}

  /**
   * Show an i18n key (or raw text via `raw`). Undoable toasts stay 10 s and keep `undoToken`;
   * any other toast clears a pending undo. Non-undoable duration is 3.5 s.
   */
  show(key: I18nKey | string, undoable = false, ms?: number): void {
    this.s.toast = key;
    clearTimeout(this.timer);
    if (!undoable) {
      this.s.undoSlot = null;
      this.s.undoToken = null;
    }
    this.s.toastLeft = ms ?? (undoable ? TOAST_UNDO_MS : TOAST_MS);
    this.s.toastAt = nowMs();
    this.arm(this.s.toastLeft);
  }

  private arm(ms: number): void {
    this.timer = setTimeout(() => {
      this.s.toast = null;
      this.s.undoSlot = null;
      this.s.undoToken = null;
    }, ms);
  }

  /** Pause on hover/focus; the remaining time floors at 600 ms. */
  hold(): void {
    if (!this.s.toast || !this.s.toastAt) return;
    clearTimeout(this.timer);
    this.s.toastLeft = Math.max(TOAST_MIN_LEFT_MS, this.s.toastLeft - (nowMs() - this.s.toastAt));
    this.s.toastAt = 0;
  }

  /** Resume after hover/focus leaves. */
  release(): void {
    if (!this.s.toast || this.s.toastAt) return;
    this.s.toastAt = nowMs();
    this.arm(this.s.toastLeft);
  }

  clear(): void {
    clearTimeout(this.timer);
    this.s.toast = null;
  }

  /** Stop the auto-hide timer without touching the visible toast (shutdown / teardown). */
  dispose(): void {
    clearTimeout(this.timer);
    this.timer = undefined;
  }
}

export interface DialogSpec {
  kind: 'prompt' | 'confirm';
  title: string;
  body?: string;
  ok?: string;
  value?: string;
  placeholder?: string;
  danger?: boolean;
}

/**
 * One `<dialog class="tik-dlg">` driven by this store. `open()` returns a Promise: a string
 * (or null) for prompts, a boolean for confirms. Opening a second dialog settles the first.
 */
export class DialogStore {
  spec = $state<DialogSpec | null>(null);
  private settleFn: ((v: string | boolean | null) => void) | null = null;

  get isOpen(): boolean {
    return this.spec !== null;
  }

  open(spec: Omit<DialogSpec, 'kind'> & { kind: 'prompt' }): Promise<string | null>;
  open(spec: Omit<DialogSpec, 'kind'> & { kind: 'confirm' }): Promise<boolean>;
  open(spec: DialogSpec): Promise<string | boolean | null> {
    if (this.spec) this.settle(this.spec.kind === 'confirm' ? false : null);
    this.spec = spec;
    return new Promise((resolve) => {
      this.settleFn = resolve;
    });
  }

  /** Resolve the pending promise and close. */
  settle(value: string | boolean | null): void {
    const fn = this.settleFn;
    this.settleFn = null;
    this.spec = null;
    fn?.(value);
  }

  /** Cancel = null for prompts, false for confirms. */
  cancel(): void {
    this.settle(this.spec?.kind === 'confirm' ? false : null);
  }

  /** Prompt submit: empty values are ignored (the OK button is disabled while empty). */
  submit(value?: string): void {
    const spec = this.spec;
    if (!spec) return;
    if (spec.kind === 'confirm') {
      this.settle(true);
      return;
    }
    const v = (value ?? '').trim();
    if (v) this.settle(v);
  }
}

export const dialog = new DialogStore();

export const prompt = (spec: Omit<DialogSpec, 'kind'>): Promise<string | null> => dialog.open({ ...spec, kind: 'prompt' });
export const confirm = (spec: Omit<DialogSpec, 'kind'>): Promise<boolean> => dialog.open({ ...spec, kind: 'confirm' });
